import { useState, useMemo } from 'react';

interface CashDragCalculatorProps {
  onStopLeakClick?: () => void;
}

export default function CashDragCalculator({ onStopLeakClick }: CashDragCalculatorProps) {
  const [cashAmount, setCashAmount] = useState(100000);

  const theftSince2021 = useMemo(() => cashAmount * 0.22, [cashAmount]);
  const projectedLoss = useMemo(() => cashAmount * 0.35, [cashAmount]);
  const mortgageMonths = useMemo(() => Math.round((theftSince2021 + projectedLoss) / 2500), [theftSince2021, projectedLoss]);

  const formatCurrency = (value: number) =>
    value.toLocaleString('en-US', { maximumFractionDigits: 0 });

  const handleStopLeakClick = () => {
    if (onStopLeakClick) {
      onStopLeakClick();
    }
  };

  // Calculate slider fill percentage
  const sliderPercent = ((cashAmount - 10000) / (1000000 - 10000)) * 100;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Headline - Outside the card */}
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-5xl sm:text-6xl md:text-7xl font-black text-white tracking-tight leading-tight">
          See How Much Your Cash<br />Is Losing to Inflation
        </h1>
      </div>

      {/* Calculator Card */}
      <div className="bg-[#151c2c]/80 backdrop-blur-md border border-gray-700/50 rounded-2xl p-6 sm:p-8 lg:p-10 shadow-2xl">

        {/* Slider Section */}
        <div className="mb-8 sm:mb-10">
          {/* STEP 1 label - now contains the subheadline text */}
          <p className="text-green-400 text-lg font-bold uppercase tracking-widest mb-2">
            STEP 1: MOVE THE SLIDER TO YOUR CURRENT CASH BALANCE AND DISCOVER HOW MUCH PURCHASING POWER YOU'VE ALREADY LOST—AND WHAT YOU'LL LOSE IF NOTHING CHANGES.
          </p>
          <div className="flex items-center justify-between mb-3">
            <label className="text-gray-300 text-sm font-semibold uppercase tracking-wider">
              TOTAL CASH SAVINGS
            </label>
            <div className="text-white text-2xl sm:text-3xl font-black tabular-nums">
              ${formatCurrency(cashAmount)}
            </div>
          </div>

          {/* Custom Slider */}
          <div className="relative mt-4">
            <input
              type="range"
              min={10000}
              max={1000000}
              step={5000}
              value={cashAmount}
              onChange={(e) => setCashAmount(Number(e.target.value))}
              className="w-full h-3 rounded-full appearance-none cursor-pointer focus:outline-none"
              style={{
                background: `linear-gradient(to right, #22C55E 0%, #22C55E ${sliderPercent}%, #1f2937 ${sliderPercent}%, #1f2937 100%)`,
              }}
            />
            <style>{`
              input[type="range"]::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 28px;
                height: 28px;
                border-radius: 50%;
                background: #22C55E;
                border: 3px solid #fff;
                box-shadow: 0 0 16px rgba(34,197,94,0.5), 0 2px 8px rgba(0,0,0,0.4);
                cursor: grab;
                transition: box-shadow 0.2s;
              }
              input[type="range"]::-webkit-slider-thumb:active {
                cursor: grabbing;
                box-shadow: 0 0 24px rgba(34,197,94,0.7), 0 2px 12px rgba(0,0,0,0.5);
              }
              input[type="range"]::-moz-range-thumb {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                background: #22C55E;
                border: 3px solid #fff;
                box-shadow: 0 0 16px rgba(34,197,94,0.5), 0 2px 8px rgba(0,0,0,0.4);
                cursor: grab;
              }
              input[type="range"]::-moz-range-track {
                height: 12px;
                border-radius: 9999px;
                background: transparent;
              }
            `}</style>
          </div>

          <div className="flex justify-between mt-2">
            <span className="text-gray-500 text-xs font-medium">$10,000</span>
            <span className="text-gray-500 text-xs font-medium">$1,000,000</span>
          </div>
        </div>

        {/* Result Boxes */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mb-8">
          {/* Box A */}
          <div className="bg-[#0B1120] border border-red-900/30 rounded-xl p-5 sm:p-6 text-center relative overflow-hidden group hover:border-red-700/50 transition-colors duration-300">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 to-red-800"></div>
            <div className="absolute inset-0 bg-red-600/[0.03] group-hover:bg-red-600/[0.06] transition-colors duration-300"></div>
            <div className="relative z-10">
              <div className="text-gray-300 text-sm font-semibold leading-snug mb-3">
                Over the last 5 years, you've lost
              </div>
              <div className="text-red-500 text-3xl sm:text-4xl font-black tabular-nums mb-2 transition-all duration-150">
                -${formatCurrency(theftSince2021)}
              </div>
              <div className="text-gray-500 text-xs sm:text-sm">
                in purchasing power
              </div>
            </div>
          </div>

          {/* Box B */}
          <div className="bg-[#0B1120] border border-red-900/30 rounded-xl p-5 sm:p-6 text-center relative overflow-hidden group hover:border-red-700/50 transition-colors duration-300">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-700 to-red-900"></div>
            <div className="absolute inset-0 bg-red-600/[0.03] group-hover:bg-red-600/[0.06] transition-colors duration-300"></div>
            <div className="relative z-10">
              <div className="text-gray-300 text-sm font-semibold leading-snug mb-3">
                If you do nothing, you'll lose another
              </div>
              <div className="text-red-500 text-3xl sm:text-4xl font-black tabular-nums mb-2 transition-all duration-150">
                -${formatCurrency(projectedLoss)}
              </div>
              <div className="text-gray-500 text-xs sm:text-sm">
                over the next 5 years
              </div>
            </div>
          </div>
        </div>

        {/* Reality Check */}
        <div className="bg-[#0B1120]/80 border border-yellow-700/30 rounded-xl p-5 sm:p-6 text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <i className="ri-error-warning-fill text-yellow-500 text-lg"></i>
            <span className="text-yellow-500 text-xs font-bold uppercase tracking-widest">
              Reality Check
            </span>
          </div>
          <p className="text-gray-200 text-base sm:text-lg md:text-xl leading-relaxed">
            That is equivalent to{' '}
            <span className="text-red-500 font-black text-xl sm:text-2xl md:text-3xl">
              {mortgageMonths}
            </span>{' '}
            months of mortgage payments...{' '}
            <span className="text-white font-bold">gone forever.</span>
          </p>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          {/* STEP 2 label */}
          <p className="text-green-400 text-lg font-bold uppercase tracking-widest mb-3">
            STEP 2: Click below to stop the loss.
          </p>
          <button
            onClick={handleStopLeakClick}
            className="inline-flex items-center gap-2 px-8 sm:px-10 py-3.5 sm:py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold text-base sm:text-lg rounded-lg transition-all duration-300 shadow-lg hover:shadow-green-600/40 cursor-pointer whitespace-nowrap"
          >
            PROTECT MY CASH
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
